import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

export interface SavedSearch {
  id: string;
  query: string;
  filters?: {
    radius?: string;
    region?: string;
    suburb?: string;
    sortBy?: string;
  };
  createdAt: string;
  notifyOnNew?: boolean;
}

const SAVED_SEARCHES_KEY = "ozknews_saved_searches";

export function useSavedSearch() {
  const { user } = useAuth();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);

  // Load saved searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(SAVED_SEARCHES_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSavedSearches(Array.isArray(parsed) ? parsed : []);
      } catch {
        setSavedSearches([]);
      }
    }
  }, []);

  const saveSearch = (search: Omit<SavedSearch, "id" | "createdAt">) => {
    const newSearch: SavedSearch = {
      ...search,
      id: `search-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      createdAt: new Date().toISOString(),
    };

    const updated = [...savedSearches, newSearch];
    setSavedSearches(updated);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated));

    return newSearch;
  };

  const deleteSearch = (id: string) => {
    const updated = savedSearches.filter((s) => s.id !== id);
    setSavedSearches(updated);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated));
  };

  const toggleNotify = (id: string) => {
    const updated = savedSearches.map((s) =>
      s.id === id ? { ...s, notifyOnNew: !s.notifyOnNew } : s
    );
    setSavedSearches(updated);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated));
  };

  const isSearchSaved = (query: string, filters?: SavedSearch["filters"]) => {
    return savedSearches.some(
      (s) =>
        s.query === query &&
        JSON.stringify(s.filters) === JSON.stringify(filters)
    );
  };

  return {
    savedSearches,
    saveSearch,
    deleteSearch,
    toggleNotify,
    isSearchSaved,
  };
}
